import { reactive, toRaw } from "vue";
import { has } from "../../../../utils/menu-context";
import { Col, Row } from "ant-design-vue";
import store from "../../../../store/index";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import i18n from "../../../../i18n";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import { createColumnInput, createColumnSelect } from "../../../../components/table/c-table.logic";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView
} from "../../../../utils/form";
import {
    createFieldInput,
    createFieldSelect
} from "../../../../components/input/template-builder/template-builder.logic";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { createLoading } from "../../../../utils/loading";
import { required, validateError } from "../../../../utils/validate";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../utils/page";
import lodash from "lodash";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/master/samplingMonitorProduct";
export const routerPrefix = "sampling-monitor-products";

export const callSave = ({ form }) => {
    return store.dispatch(`${actionPrefix}/save`, { data: form });
};
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const callLabType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LAB_TYPE", value: searchQuery });
export const callSamplingMonitorType = ({ searchQuery }) => store.dispatch("resource/v1/master/samplingMonitorType/cacheFindAll", { searchQuery });
export const callSamplingMonitorSpecies = ({ searchQuery }) => store.dispatch("resource/v1/master/samplingMonitorSpecies/cacheFindAll", { searchQuery });
export const callSamplingMonitorSourceType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_MONITOR_SOURCE_TYPE", value: searchQuery });

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onSearch = search => {
    setIsLoading(true);
    const { page, isSet = true } = search;
    const { where = [] } = page;
    window.onSearch?.({ page });

    return callSearch({
        criteria: {
            ...page,
            select: ["**"],
            where: [...where],
            data: []
        }
    })
        .then(page => (isSet ? (state.page = { ...page }) : page))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then(record => onClickView({ record, routerPrefix }));
        })
        .catch(error => validateError({ error }));
};

export const initState = async ({ props, data }) => {
    const menuContext = props.menuContext;
    state.columns = createColumns({ props, data });

    const onSearchCustom = search => onSearch({ ...search, props });

    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) => onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: onSearchCustom, setIsLoading })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
            onClickArchiveAll({
                 rowSelection, callArchiveAll, state, onSearch: onSearchCustom, setIsLoading })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) => onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: onSearchCustom, setIsLoading })
        }),
        createManageExportCsv({ menuContext, callData: ({ page }) => onSearch({ props, page: { ...lodash.cloneDeep({ ...page, page: 0, size: 1000000 }) }, isSet: false }) })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({ menuContext, onClick: ({ record }) => onClickEdit({ record, routerPrefix }) })
    ];
};

export const createColumns = () => {
    const columns = [
        createColumnInput({
            title: () => i18n.global.t("model.samplingMonitorProduct.code"),
            dataIndex: "code",
            align: "center"
        }),
        createColumnInput({
            title: () => i18n.global.t("model.samplingMonitorProduct.name"),
            dataIndex: "name",
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingMonitorProduct.refSamplingMonitorType"),
            dataIndex: "refSamplingMonitorType.name",
            customRender: ({ record }) => <div>{record.refSamplingMonitorType?.code} - {record.refSamplingMonitorType?.name}</div>,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: callSamplingMonitorType,
                    renderLabel: ({ item }) => <div>{item.code} - {item.name}</div>,
                };
            },
            width: 180,
            align: "center",
            search: {
                name: "refSamplingMonitorType.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingMonitorProduct.refSamplingMonitorSourceType"),
            dataIndex: "refSamplingMonitorSourceType.value",
            customRender: ({ record }) => <div>{record.refSamplingMonitorSourceType?.value}</div>,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: callSamplingMonitorSourceType,
                    renderLabel: ({ item }) => <div>{item.value}</div>,
                };
            },
            align: "center",
            search: {
                name: "refSamplingMonitorSourceType.id"
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.samplingMonitorProduct.refSamplingMonitorSpecies"),
            dataIndex: "refSamplingMonitorSpecies.name",
            customRender: ({ record }) => <div>{record.refSamplingMonitorSpecies?.code} - {record.refSamplingMonitorSpecies?.name}</div>,
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: callSamplingMonitorSpecies,
                    renderLabel: ({ item }) => <div>{item.code} - {item.name}</div>,
                };
            },
            align: "center",
            search: {
                name: "refSamplingMonitorSpecies.id"
            }
        }),
    ];
    return columns;
};


export const state = reactive({
    columns: [],
    page: createPage({
        options: {
            select: ["*"]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } }
});

export const createForm = () => {
    return {
        refSamplingMonitorType: null,
        refSamplingMonitorSpecies: null,
        refSamplingMonitorSourceType: null,
        code: null,
        name: null,
    };
};

export const createRules = () => {
    return {
        refSamplingMonitorType: [required],
        refSamplingMonitorSpecies: [required],
        refSamplingMonitorSourceType: [required],
        code: [required],
        name: [required],
    };
};

export const createField = ({ props, data }) => {
    const viewOnly = props.viewOnly;
    const useForm = data.useForm;
    const modelRef = useForm.modelRef;
    return [
        createFieldInput({
            key: `code`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.samplingMonitorProduct.code'),
            inputProp: () => ({
                value: modelRef.code,
                'onUpdate:value': value => modelRef.code = value,
                disabled: true,
            })
        }),
        createFieldSelect({
            key: `refSamplingMonitorType`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.samplingMonitorProduct.refSamplingMonitorType"),
            inputProp: () => ({
                value: !viewOnly ? modelRef.refSamplingMonitorType : modelRef.refSamplingMonitorType,
                "onUpdate:value": value => {
                    modelRef.refSamplingMonitorType = value
                    combineCode({ modelRef })
                },
                list: callSamplingMonitorType,
                disabled: viewOnly,
                renderLabel: ({ item }) => <div>{ item.code } - { item.name}</div>,
            }),
        }),
        createFieldSelect({
            key: `refSamplingMonitorSpecies`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.samplingMonitorProduct.refSamplingMonitorSpecies"),
            inputProp: () => ({
                value: modelRef.refSamplingMonitorSpecies,
                "onUpdate:value": value => {
                    modelRef.refSamplingMonitorSpecies = value
                    combineCode({ modelRef })
                },
                list: callSamplingMonitorSpecies,
                renderLabel: ({ item }) => <div>{ item.code } - { item.name}</div>,
                disabled: viewOnly,
            }),
            changeLog: { getValue: ({ record }) => record.refSamplingMonitorSpecies?.code }
        }),
        createFieldSelect({
            key: `refSamplingMonitorSourceType`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.samplingMonitorProduct.refSamplingMonitorSourceType"),
            inputProp: () => ({
                value: modelRef.refSamplingMonitorSourceType,
                "onUpdate:value": value => {
                    modelRef.refSamplingMonitorSourceType = value
                    combineCode({ modelRef })
                },
                list: callSamplingMonitorSourceType,
                renderLabel: ({ item }) => <div>{ item.key } - { item.value}</div>,
                disabled: viewOnly,
            }),
            changeLog: { getValue: ({ record }) => record.refSamplingMonitorSourceType?.value }
        }),
        createFieldInput({
            key: `name`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('model.samplingMonitorProduct.name'),
            inputProp: () => ({
                value: modelRef.name,
                'onUpdate:value': value => modelRef.name = value,
                disabled: viewOnly,
            })
        }),
    ];
};

const combineCode = ({ modelRef }) => {
    if (!modelRef) return;
    let code = "";
    let name = "";
    if (modelRef?.refSamplingMonitorType) { code += modelRef.refSamplingMonitorType?.code }
    if (modelRef?.refSamplingMonitorSpecies) {
        code += modelRef.refSamplingMonitorSpecies?.code
        name += modelRef.refSamplingMonitorSpecies?.name
    }
    if (modelRef?.refSamplingMonitorSourceType) {
        code += modelRef.refSamplingMonitorSourceType?.key
        name += modelRef.refSamplingMonitorSourceType?.value
    }
    if (code) modelRef.code = code
    if (name) modelRef.name = name
}

export const createFormTitle = ({ data }) => {
    return !data.useForm?.modelRef?.id ? i18n.global.t("button.create") : `${data.useForm.modelRef.code} ${data.useForm.modelRef.name}`;
};

export const createAppPageHeaderDefault = ({ props, data }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;

    const { id, isActive } = data.useForm.modelRef;

    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && has({ menuContext, key: "canEdit" })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ data }), classStyle: "m-1" }));
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }

    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise.then(page => initUseForm({ data, page, routerPrefix })).finally(() => setIsLoading(false));
};

export const initForm = async ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm();
    data.rules = createRules();
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });

    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};
